// Message.js
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { AppointmentMessage, AppointmentRequestMessage } from '../../components/messageTypes/appointmentRequestMessage';
import { ApptMessage } from '../../components/messageTypes/apptMessage';
import { BaseMessage } from '../../components/messageTypes/baseMessage';
import { EventMessage } from '../../components/messageTypes/eventMessage';
import { useGroupChatContext } from '../../context/groupchatContext';
import { ChangeRequestMessage } from '../../components/messageTypes/changeRequestMessage';
import { FaSpinner } from 'react-icons/fa';
import { isPatientChatMessage, TimeAgo } from '../../helpers/utility';
import { ReferralRequestMessage } from '../../components/messageTypes/referralRequestMessage';
import { ReminderMessage } from '../../components/messageTypes/reminderMessage';
import { VezoSessionMessage } from '../../components/messageTypes/vezoSessionMessage';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import ReturnMessageToReply from "./returnReplyMessageWidget";
import { HiExclamation } from "react-icons/hi";
import { useForm, useWatch } from 'react-hook-form';
import { InputField } from '../../components/reactHookFormFields/inputField';
import { useMarkMessageAcknowledgedMutation, useSendMessageMutation } from '../../services/chatService';
import { toast } from 'react-toastify';
import MainPageDialog from '../../components/dialog';
import { TriageNotePopup } from '../Triage-Module/triageNotePopup';
import { TaskMessage } from '../../components/messageTypes/taskMessage';
import TaskForm from '../../components/taskForm';

const Message = React.memo(({ msg }) => {

  const [openTaskPopup, setOpenTaskPopup] = useState({isOpen: false, data: null});
  const [acknowledgingMessage, setAcknowledgingMessage] = useState(false);
  const [openTriageNotePopup, setOpenTriageNotePopup] = useState({isOpen: false, data: null});

  const groupChatContext = useGroupChatContext();
  const buttonRef = useRef(null);

  const {authData, setSelectedMessageToReply, isPrintMessage, setIsPrintMessage, selectedMessages, messages, setMessages,
    setSelectedMessages, setSearchedMessageThread, highlightedMessageId, setHighlightedMessageId, openDropdownId, setOpenDropdownId} = useMemo(() => ({
    authData: groupChatContext.authData,
    setSelectedMessageToReply: groupChatContext.setSelectedMessageToReply,
    isPrintMessage: groupChatContext.isPrintMessage,
    setIsPrintMessage: groupChatContext.setIsPrintMessage,
    selectedMessages: groupChatContext.selectedMessages,
    setSelectedMessages: groupChatContext.setSelectedMessages,
    setSearchedMessageThread: groupChatContext.setSearchedMessageThread,
    setHighlightedMessageId: groupChatContext.setHighlightedMessageId,
    highlightedMessageId: groupChatContext.highlightedMessageId,
    messages: groupChatContext.messages,
    setMessages: groupChatContext.setMessages,
    openDropdownId: groupChatContext.openDropdownId,
    setOpenDropdownId: groupChatContext.setOpenDropdownId,
  }));

  const MessageActionOptions = [
    { slug: 'reply', label: 'Reply' },
    { slug: 'print', label: 'Print' },
    { slug: 'acknowledged', label: 'Mark Acknowledged' },
  ];

  if(authData?.is_provider && (msg?.message?.type != 'event' || msg?.message?.event_type == 'change_status')){
    MessageActionOptions.push({ slug: 'triage_note', label: 'Create Triage' });
  }

  if(authData?.is_provider && (msg?.message?.type != 'event' && msg?.message?.media?.length)){
    MessageActionOptions.push({ slug: 'create_task', label: 'Create Task' });
  }

  const {
    control,
    formState: { errors },
  } = useForm();

  const {
    ...formHandler
  } = useForm();


  const assigneeProviderVal = useWatch({ control: formHandler.control, name: "assignee_provider" });

  const [sendMessage, {isLoading: isSendingMsg}] = useSendMessageMutation();
  const onSubmitTaskForm = async(data) => {

    const {patient, auto_complete_all} = formHandler.getValues();
    const payload = {
      pid: msg.pid,
      message_id: msg.id,
      message: {
        text: msg?.message?.text,
        type: "event",
        event_type: "create_task",
        media: msg?.message?.media,
        data: {
          title: msg?.message?.media[0]?.title,
          auto_complete_all,
          // pid: selectedChat?.pid ? selectedChat?.pid : patient.pid,
          user_id: assigneeProviderVal.map(item => item.value).join(','),
        }
      },
      mentions: "",
    };


    try {
      const res = await sendMessage(payload);
      if(res?.data?.code === 0) {
        toast.success(res.data.msg);
        const msgs = [res?.data?.data, ...messages.data];
        setMessages({...messages, data: msgs});
        setOpenTaskPopup({isOpen: false, data: null})
      }
      else {
        toast.error(res.data.msg);
      }
    }
    catch (error) {
      toast.error("Failed to send message.");
    }
  }

  const footerList = [
    {
      title: "Cancel",
      type: "cancel",
      action: () => {
        setOpenTaskPopup({isOpen: false, data: null})
      },
    },
    {
      title: "Save",
      type: "save",
      loading: isSendingMsg,
      action: formHandler.handleSubmit(onSubmitTaskForm),
    },
  ];

  if(assigneeProviderVal?.length){
    footerList.push({
      title: "Discard",
      type: "delete",
      action: () => {
        formHandler.reset({
          assignee_provider: [],
          auto_complete_all: false,
        });
      },
    })
  }


  const [handleAcknowledgeMessageTrigger] = useMarkMessageAcknowledgedMutation();

  const handleCheckboxChange = (msgId) => {
    if (selectedMessages.includes(msgId)) {
      setSelectedMessages(selectedMessages.filter(id => id !== msgId));
    } else {
      setSelectedMessages([...selectedMessages, msgId]);
    }
  };

  const isLoggedInUser = () => {
    return (msg?.nurse_id == authData?.id || msg?.user_id == authData?.id)
  }

  const returnMessageWidget = (msg)=> {
    
    if(msg?.message?.type == 'message' || !msg?.message?.type){
      return <BaseMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else if(msg?.message.event_type == 'note_signed'){
      return <EventMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else if(msg?.message.event_type == 'request_appointment'){
      return <AppointmentRequestMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else if(msg?.message.event_type == 'appointment'){
      return <ApptMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else if(msg?.message?.event_type == 'vezo_session'){
      return <VezoSessionMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else if(msg?.message.event_type == 'change_status'){
      return <ChangeRequestMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else if(msg?.message.event_type == 'referral_requested'){
      return <ReferralRequestMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else if(msg?.message.event_type == 'twilio_call'){
      return <ReminderMessage msg={msg} />
    }
    else if(msg?.message?.event_type == 'broadcast_message'){
      return <BaseMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else if(msg?.message.event_type == 'create_task'){
      return <TaskMessage msg={msg} isLoggedInUser={isLoggedInUser} />
    }
    else {
      return null;
    }
  }

  const isShowMessageElipsis = () => {
    try {
      if (['twilio_call']?.includes(msg?.message?.event_type)) {
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error in isShowMessageElipsis:', error);
      return false;
    }
  }

  const handleReplyClick = (msg) => {
    setSelectedMessageToReply(msg);
  };

  const handleAcknowledgeClick = async (msg) => {
    try {
      setAcknowledgingMessage(true);
      handleAcknowledgeMessageTrigger({ message_id: msg?.id })
      .then((res) => {
        const { code, msg } = res?.data || {};
        if(code === 1 || code === "1") {
          toast.success(msg || "Message acknowledged successfully");
        }
      })
      .catch((error) => {
        // toast.error("Error while acknowledging message");
        console.error("Error while handleAcknowledgeClick:", error);
      })
      .finally(() => {
        setAcknowledgingMessage(false);
      });
    } catch (error) {
      setAcknowledgingMessage(false);
      toast.error("Error acknowledging message");
      console.error("Error in handleAcknowledgeClick:", error);
    }
  }

  const moveToMessage = (msg) =>{
    setSearchedMessageThread({thread_id: msg?.id });
    setHighlightedMessageId(msg?.id);
  }

  const handleOptionClick = useCallback((opt, msg) => {
    try {
      setOpenDropdownId(null);
      switch (opt.slug) {
        case 'reply':
          handleReplyClick(msg);
          break;
        case 'print':
          setIsPrintMessage(true);
          break;
        case 'create_task':
          setOpenTaskPopup({isOpen: true, data: msg});
          break;
        case 'acknowledged':
          handleAcknowledgeClick(msg);
          break;
        case 'triage_note':
          let data = msg?.message;
          if(msg?.message?.event_type == 'change_status'){
            data = {
              ...msg?.message,
              text: msg?.message?.data?.text,
            }
          }
          setOpenTriageNotePopup({isOpen: true, data: data});
          break;
        default:
          console.warn("Unhandled option:", opt.slug);
          break;
      }
    } catch (error) {
      console.error("Error in handleOptionClick:", error);
    }
  }, [setOpenDropdownId, handleReplyClick, setIsPrintMessage, handleAcknowledgeClick]);

  const getDropdownPosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const buttonCenter = buttonRect.left + (buttonRect.width / 2);
      const screenHalf = window.innerWidth / 2;
      return buttonCenter > screenHalf ? 'right-0' : 'left-0';
    }
    return 'right-0';
  }

  return (
    <>
      <div className={`flex gap-3 pb-4 ${isLoggedInUser() ? 'flex-row-reverse text-start' : 'w-full'} ${isShowMessageElipsis() ? '' : 'justify-center'} your-parent-container-class`}>
        {isPrintMessage && (
          <div className="p-1">
            <input
              type="checkbox"
              checked={selectedMessages.includes(msg?.id)}
              onChange={() => handleCheckboxChange(msg?.id)}
            />
          </div>
        )}
        <div className={`${isLoggedInUser() ? 'text-right' : 'text-left'}`}>
          {isShowMessageElipsis() ? 
            <h2 className=" text-sm font-semibold">
              {isLoggedInUser() ? null : msg?.sender}
              <span className="text-xs text-gray-500 ml-1">
                {!isLoggedInUser() && msg?.sender=='eSync' && msg?.status != 'done' ? <FaSpinner className="animate-spin text-green-300-500 ml-0 inline" size={15} /> : msg?.status == 'sending' ? 'Sending...' : msg?.status == 'queued' ? 'Sending...' : msg?.created ? <TimeAgo date={new Date(msg?.created)} /> : null}
              </span>
            </h2>
            : null
          }
          <div className='flex'>
            {/* <div className={`${msg?.message?.parent_message? 'rounded-xl bg-blue-100   p-2 shadow' : ''}`}> */}
            <div className={`${msg?.message?.parent_message? (isLoggedInUser() ? 'rounded-xl bg-blue-100   p-2 shadow' : 'rounded-xl bg-light-gray-f1 p-2 shadow'):''}`}>
                {msg?.message?.parent_message && (
                  <div id={msg?.id} className={`${isShowMessageElipsis() ? 'mt-1': ''} flex flex-col gap-2 text-base font-sans`}
                  onClick={() => moveToMessage(msg?.message?.parent_message)}
                  >
                      <div className='p-2 bg-white rounded-xl shadow border-t border-gray-200 relative'>
                        <div className='cursor-pointer'>
                            <h2 className=" text-sm flex justify-start font-semibold">
                            {(msg?.message?.parent_message?.nurse_id == authData?.id || msg?.message?.parent_message?.user_id == authData?.id)?  "You" : msg?.message?.parent_message?.sender}
                            </h2>
                            <div className="flex justify-start">
                                <ReturnMessageToReply msg={msg?.message?.parent_message}/>
                                {(msg?.message?.parent_message?.message?.urgent || msg?.message?.parent_message?.urgent) && (
                                <HiExclamation title="Urgent Message" className="text-red-500 mt-1" />
                                )}
                            </div>
                        </div>
                      </div>
                  </div>
                )}
                <div className="flex">
                  <div id={msg?.id} className={`${isShowMessageElipsis() ? 'mt-1': ''} flex flex-col gap-2 text-base font-sans ${highlightedMessageId === msg?.id ? 'border-[1px] border-black rounded-md' : ''}`}>
                    {returnMessageWidget(msg)}
                  </div>
                </div>
            </div>
            {isShowMessageElipsis() ?
              <div className="relative inline-block">
                <button className="ml-2" ref={buttonRef} >
                  <EllipsisVerticalIcon className="h-6 w-6 text-blue-500" onClick={() => setOpenDropdownId(openDropdownId === msg?.id ? null : msg?.id)}/>
                </button>
                {openDropdownId === msg?.id && (
                    <div onMouseLeave={() => setOpenDropdownId(null)} className={`absolute ${getDropdownPosition()} mt-1 bg-white bottom-full shadow-lg rounded-lg z-[998] w-max text-left min-w-[100px]`}>
                        <ul>
                        {MessageActionOptions.filter(option => {
                            try {
                              return !(isPrintMessage && option.slug === 'print') && (option.slug !== 'acknowledged' || (msg?.urgent && (!msg?.acknowledged_at && !msg?.acknowledged_by_nurse_id && !msg?.acknowledged_by_user_id)));
                            } catch (error) {
                              console.error("Error filtering options:", error);
                              return false;
                            }
                          })
                          .map(option => {
                            try {
                              return (
                                <li 
                                  key={option.slug} 
                                  onClick={() => handleOptionClick(option, msg)}
                                  className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                                >
                                  {option.label}
                                </li>
                              );
                            } catch (error) {
                              console.error("Error mapping option:", error);
                              return null;
                            }
                          })}
                        </ul>
                    </div>
                )}
              </div>
              : null
            }
          </div>
        </div>
      </div>

      {openTriageNotePopup?.isOpen ? 
        <MainPageDialog
            title="Create Triage Note"
            onCancelHandler={() => setOpenTriageNotePopup({isOpen: false, data: null})}
            modalSize="md"
            maxHeight={true}
            customHeight={"min-h-[60vh]"}
            component={
              <TriageNotePopup closeTriagePopup={() => setOpenTriageNotePopup({isOpen: false, data: null})} data={openTriageNotePopup?.data} />
            }
            footer={false}
        />
        : null
      }

      {openTaskPopup?.isOpen ?
        <MainPageDialog
          title="Create Task"
          onCancelHandler={() => setOpenTaskPopup({isOpen: false, data: null})}
          modalSize="md"
          maxHeight={true}
          customHeight={"min-h-[60vh]"}
          footerList={footerList}
          footer={true}
        >
          <TaskForm formHandler={formHandler} />
        </MainPageDialog>
        : null
      }
    </>
  );
});

export default Message;